import useAccountManagementStore from "../../stores/accountManagementStore";
import useDataStore, { DataStoreType } from "../../stores/dataStore";
import useMainStore from "../../stores/mainStore";
import syncChangesToCloud from "../backend/syncChangesToCloud";

const dbName = "dataStore";
const defaultName = "default";

const newData: DataStoreType["data"] = {
    folders: {},
    notes: {},
    tags: {},
};

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName, 1);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(defaultName)) {
                db.createObjectStore(defaultName);
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/**
 * Saves the dataStore to IndexedDB for a given userID.
 * If userID is undefined, it defaults to "default".
 * @param userID The ID of the user to save the dataStore for.
 */
export async function saveDataStore(userID?: string, skipCloudSync = false) {
    if (!skipCloudSync) {
        // skip this because it needs to reach cloudSync for it to removeChangesMade
        useMainStore.getState().syncFunctions.addChangesMade("tableChanged");
    }

    const id = userID || defaultName;

    const storeData = { ...useDataStore.getState().data };
    const dataString = JSON.stringify(storeData);

    const db = await openDB();
    const tx = db.transaction(defaultName, "readwrite");
    tx.objectStore(defaultName).put(dataString, id);
    return await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = reject;
        console.log("Saved Data Store");
        const userData = useAccountManagementStore.getState().userData;

        // Compare local with cloud and only update if there are conflicts
        if (!userData) return; // Don't sync to cloud if not logged in

        if (skipCloudSync) return;
        syncChangesToCloud();
    });
}

/**
 * Loads the dataStore from IndexedDB for a given userID.
 * If userID is undefined, it defaults to "default".
 * @param userID The ID of the user to load the dataStore for.
 * @returns The dataStore data as a JSON string, or null if there was an error.
 */
export async function loadDataStore(userID?: string): Promise<string | null> {
    const id = userID || defaultName;

    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(defaultName, "readonly");
        const request = tx.objectStore(defaultName).get(id);
        request.onsuccess = () =>
            resolve(request.result ? request.result : JSON.stringify(newData));
        request.onerror = () => reject(request.error);
    });
}

/**
 * Removes the dataStore from IndexedDB for a given userID.
 * If userID is undefined, it defaults to "default".
 * @param userID The ID of the user to remove the dataStore for.
 * @returns A promise that resolves when the dataStore has been removed.
 */
export async function removeFromDataStore(userID?: string): Promise<void> {
    const id = userID || defaultName;

    const db = await openDB();
    const tx = db.transaction(defaultName, "readwrite");
    tx.objectStore(defaultName).delete(id);
    await new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = reject;
    });
}
